'use client';

import { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Box, Modal, Button, IconButton, Typography, Avatar } from '@mui/material';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import { CloseIcon } from 'src/theme/core/components/mui-x-data-grid';

import { Form, Field } from 'src/components/hook-form';
import { useLocale } from 'src/locales';
import { useGetAllFleets } from 'src/api/Order/useFleet';
import { getLabelForlanguage, getTruckType, notify, saudiCitiesName } from 'src/utils/helper';
import { createSchemas } from 'src/utils/use-validations';
import { max } from 'moment';
import { useGetSaudiCityNames, useRequestQuotation } from 'src/api/business/useBusiness';

const OrderFrequency = [
  { label: 'Daily', value: 'Daily' },
  { label: 'Monthly', value: 'Monthly' },
];

export function QuotationRequestForm({ open, onClose, formValues }) {
  const { t, locale } = useLocale();
  const { QuotationRequestSchema } = createSchemas();

  const { data: fleets } = useGetAllFleets();
  const { data: citiesList } = useGetSaudiCityNames();
  const { mutateAsync: requestQuotation, isPending } = useRequestQuotation();

  const defaultValues = {
    AvgGPS: '',
    AvgWeight: '',
    TypeOfLoad: '',
    ExpectedNumberOfShipments: '',
    OrderFrequency: '',
    AdditionalNotes: '',
    routes: [
      {
        truckSize: '',
        trucktype: '',
        pickupCity: '',
        dropOffCity: [],
      },
    ],
  };

  const methods = useForm({
    resolver: zodResolver(QuotationRequestSchema),
    defaultValues,
    mode: 'all',
  });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isSubmitting, isValid },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'routes',
  });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const truckDetails = data.routes.map((route) => ({
        Truck_Size: route.truckSize?.name || '',
        Truck_Type: route.trucktype?.name || '',
        Pickup_City: route.pickupCity?.value || '',
        Drop_City: route.dropOffCity?.map((city) => city.value),
      }));

      const payload = {
        company: formValues?.company,
        email: formValues?.email,
        mobile: formValues?.mobile,
        service: '',
        language: locale,
        TruckDetails: truckDetails,
        AvgGPS: data.AvgGPS,
        AvgWeight: data.AvgWeight,
        TypeOfLoad: data?.TypeOfLoad,
        ExpectedNumberOfShipments: data?.ExpectedNumberOfShipments,
        OrderFrequency: data?.OrderFrequency?.value,
        AdditionalNotes: data?.AdditionalNotes,
      };

      console.log('FORM DATA', payload);
      requestQuotation(payload).then((response) => {
        if (response?.success) {
          notify(
            'success',
            t('Your_request_has_been_submitted_successfully') ||
              'Your request has been submitted successfully'
          );
          onClose?.();
          reset();
        } else {
          notify(
            'error',
            t('There_was_an_error_submitting_your_request') ||
              'There was an error submitting your request'
          );
        }
      });
    } catch (error) {
      console.log(error);
    }
  });

  const addNewRow = () => {
    append({
      truckSize: '',
      trucktype: '',
      pickupCity: '',
      dropOffCity: [],
    });
  };

  // Function to transform a single city object
  const transformCityOptions = (cities, needAllOptions = false) => {
    if (!Array.isArray(cities) || !cities?.length) return [];
    const arr = cities.map((cityObj) => {
      const { label, labelAr, key } = cityObj;
      return {
        label: locale === 'ar' ? labelAr : label,
        value: label,
        id: key,
      };
    });

    return needAllOptions
      ? [
          {
            value: 'Major Cities & Regions in KSA',
            label:
              locale === 'ar'
                ? 'المدن والمناطق الرئيسية في السعودية'
                : 'Major Cities & Regions in KSA',
            key: 1,
          },
          {
            value: 'All GCC Countries',
            label: locale === 'ar' ? 'جميع دول الخليج' : 'All GCC Countries',
            key: 2,
          },
          ...arr,
        ]
      : arr;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.modal}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 3,
            alignItems: 'center',
          }}
        >
          <Typography variant="h3">{t('Request_Quotation')}</Typography>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Form methods={methods} onSubmit={onSubmit}>
          {/* Dynamic Truck Rows */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography variant="h6" m={0}>
              {t('Truck_and_Route_Details')}
            </Typography>

            {fields.map((item, index) => {
              const selectedTruckSize = watch(`routes.${index}.truckSize`);

              return (
                <Box
                  key={item.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    {/* Action Buttons */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <IconButton
                        color="error"
                        onClick={() => remove(index)}
                        disabled={fields.length === 1}
                      >
                        <RemoveCircleOutlineIcon />
                      </IconButton>

                      {index === fields.length - 1 && (
                        <IconButton color="primary" onClick={addNewRow}>
                          <AddCircleOutlineIcon />
                        </IconButton>
                      )}
                    </Box>

                    {/* Truck Size */}
                    <Field.Select
                      name={`routes.${index}.truckSize`}
                      label={t('Truck_Size')}
                      placeholder={t('Truck_Size')}
                      options={fleets?.success || []}
                      isImage
                      labelKey={getLabelForlanguage('name')}
                      imageKey="imagePaths.logo"
                      sx={{ flex: '1 1 220px' }}
                      onChange={(data) => {
                        setValue(`routes.${index}.trucktype`, '');
                      }}
                    />

                    {/* Truck Type */}
                    <Field.Select
                      name={`routes.${index}.trucktype`}
                      label={t('Truck_Type')}
                      placeholder={t('Truck_Type')}
                      labelKey={getLabelForlanguage('name')}
                      isImage
                      imageKey="imagePaths.logo"
                      sx={{ flex: '1 1 220px' }}
                      options={getTruckType(fleets?.success, selectedTruckSize)}
                      noOptionsText={t('Please_select_truck_size')}
                      renderOption={(renderProps, option) => (
                        <Box
                          component="li"
                          {...renderProps}
                          key={option.id}
                          display="flex"
                          alignItems="center"
                        >
                          <Avatar
                            src={option?.imagePaths?.logo}
                            sx={{
                              width: 45,
                              height: 45,
                              mr: 1,
                            }}
                            slotProps={{
                              img: {
                                style: {
                                  objectFit: 'contain',
                                },
                              },
                            }}
                          />

                          <Box>
                            <Typography variant="button">
                              {option[getLabelForlanguage('name')]}

                              <Typography variant="caption" sx={{ ml: 0.5 }}>
                                ({option?.maximumLoad?.trim()})
                              </Typography>
                            </Typography>

                            <Typography variant="caption" sx={{ display: 'block' }}>
                              Size {option.size}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    />

                    {/* Pickup City */}
                    <Field.Autocomplete
                      name={`routes.${index}.pickupCity`}
                      label={t('Pickup_City')}
                      options={transformCityOptions(saudiCitiesName)}
                      sx={{ flex: '1 1 220px' }}
                      labelKey="label"
                    />

                    {/* DropOff City */}
                    <Field.Autocomplete
                      name={`routes.${index}.dropOffCity`}
                      label={t('DropOff_City')}
                      options={transformCityOptions(saudiCitiesName, true)}
                      sx={{ flex: '1 1 220px', maxWidth: '400px' }}
                      checkbox
                      multiple
                    />
                  </Box>
                </Box>
              );
            })}

            {/* Mobile Number */}
            <Box gap={2} display="flex" flexWrap={'wrap'}>
              <Field.Text
                name="AvgGPS"
                label={t('Average_value_of_goods_per_shipment')}
                required
                sx={{ flex: '1 1 400px' }}
              />
              <Field.Text
                name="AvgWeight"
                label={t('Average_shipment_weight_tons')}
                required
                sx={{ flex: '1 1 400px' }}
              />
              <Field.Text
                name="TypeOfLoad"
                label={t('type_of_goods')}
                required
                sx={{ flex: '1 1 400px' }}
              />
              <Field.Text
                name="ExpectedNumberOfShipments"
                label={t('expected_number_of_shipment')}
                required
                sx={{ flex: '1 1 400px' }}
              />
              <Field.Autocomplete
                name={'OrderFrequency'}
                label={t('order_frequency')}
                options={OrderFrequency}
                required
                sx={{ flex: '1 1 400px' }}
              />
              <Field.Text
                name="AdditionalNotes"
                label={t('additional_notes')}
                sx={{ flex: '1 1 100%' }}
                multiline
                minRows={2}
              />
            </Box>

            {/* Submit */}
            <Button
              fullWidth
              color="primary"
              size="large"
              type="submit"
              variant="contained"
              disabled={isSubmitting || !isValid}
              loading={isSubmitting || isPending}
              sx={{ maxWidth: '400px', alignSelf: 'center' }}
            >
              {t('Submit')}
            </Button>
          </Box>
        </Form>
      </Box>
    </Modal>
  );
}

const styles = {
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '95%', md: '85%' },
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    p: 3,
  },
};
